import type { ResourceArticle } from '~/types/ResourceArticle'

export const psychosisHearingVoicesArticle: ResourceArticle = {
  uid: 'psychosis-hearing-voices',
  thumbnailImageUrl: 'https://cdn.hubofhope.co.uk/public/img/thumbs/RESOURCES_T%2BPTS_800x800.jpg',
  thumbnailImageAlt: 'Psychosis',
  title: 'Psychosis & hearing voices',
  supportNeedCategoryLabel: 'Psychosis',
  supportNeedCategoryUid: 'FbjPLK6K2Fh0gux4Nuyo',
  isFeatured: true,
  excerpt:
    'Sometimes stress and feelings of being overwhelmed, often linked to past traumatic experiences, can lead us to become confused or preoccupied, and we may begin to dissociate from what most people consider to be reality.',
  content: `
      <div class="prose mt-4">
        <p>
          <span class="font-bold">Sometimes stress and feelings of being overwhelmed</span>, often linked to past traumatic experiences
          (<a href="/resource/trauma-post-traumatic-stress" class="text-blue-500 underline hover:text-blue-700">see Trauma & post-traumatic stress</a>)
          can lead us to become confused or preoccupied, and we may begin to dissociate from what most people consider to be reality.
        </p>

        <p class="mt-4">
          We might hear voices when there is no-one there, or perhaps see, taste, smell or feel things that other people don't
          (sometimes called hallucinations). We might have strong fears or beliefs that other people don’t share or find strange
          (sometimes called delusions). We might have difficulty concentrating or thinking straight. We may also experience
          periods where we feel elated, 'high' and full of energy, but also often agitated and restless, and we find it hard to sleep.
          If these experiences go on for a while we can end up feeling exhausted and find it hard to motivate ourselves to do even
          basic things like washing and dressing. This exhaustion and lethargy can also be related to the medication that is
          sometimes prescribed.
        </p>

        <p>
          These experiences are sometimes referred to as psychosis, schizophrenia, or nervous breakdown. Alternating
          experiences of elation and restlessness, and low mood and lethargy are sometimes called 'bipolar' 
          (<a href="/resource/understanding-bipolar" class="text-blue-500 underline hover:text-blue-700">see Understanding bipolar experiences</a>).
        </p>

        <figure class="my-8">
          <img alt="an image of snippets of paper on a table with encouraging phrases" src="https://cdn.hubofhope.co.uk/public/img/thumbs/pexels-vie-studio-7004950_800x800.jpg"
        </figure>

        <p class="mt-10">
          <h2 class="font-semibold text-lg font-brown text-primary">Feeling isolated and alone can be very stressful</h2>
        </p>
        
        <p>
          Connecting with others with similar experiences either informally or in organized peer support groups can be a big help.
          Therapy or counseling allows us to make sense of our experiences, talk through what might help, and try things out with
          support in a calm and non-judgmental atmosphere. Some people find medication helpful.
        </p>
        
        <p class="mt-6">
          <h2 class="font-semibold text-lg text-primary">Seeking support</h2>
        </p>
        
        <p class="mb-8">
          Friends and family can be a huge support, but times of distress and confusion are stressful for everyone. It can be
          helpful to talk to people specifically trained in helping families, in meetings where the emphasis is on listening to
          everyone's understanding of the situation and working out a way forward together. One such approach is called 
          'Open Dialogue'.
        </p>
      </div>
    `
}
